import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface EventMedia {
  images: string[];
  videos: string[];
}

interface EventMediaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventDay: string;
  eventDate: string;
  media: EventMedia;
}

export const EventMediaDialog = ({ 
  open, 
  onOpenChange, 
  eventDay, 
  eventDate, 
  media 
}: EventMediaDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] p-6">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-2xl">Event media</DialogTitle>
          <DialogDescription>
            {eventDay}, {eventDate}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-180px)] pr-4">
          <div className="space-y-8">
            {/* Videos */}
            {media.videos && media.videos.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Videos</h3>
                <div className="grid gap-4">
                  {media.videos.map((video, index) => (
                    <div key={index} className="relative rounded-xl overflow-hidden bg-muted aspect-video">
                      {video.includes('youtube.com') || video.includes('youtu.be') ? (
                        <iframe
                          src={video}
                          title={`Event video ${index + 1}`}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : video.includes('vimeo.com') ? (
                        <iframe
                          src={video}
                          title={`Event video ${index + 1}`}
                          className="w-full h-full"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <video
                          src={video}
                          controls
                          className="w-full h-full"
                        >
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Images */}
            {media.images && media.images.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Photos</h3>
                <div className="grid grid-cols-2 gap-4">
                  {media.images.map((image, index) => (
                    <div
                      key={index}
                      className="relative rounded-xl overflow-hidden bg-muted aspect-[4/3] group"
                    >
                      <img
                        src={image}
                        alt={`Event photo ${index + 1}`}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {(!media.images || media.images.length === 0) && (!media.videos || media.videos.length === 0) && (
              <div className="text-center py-12 text-muted-foreground">
                No media available for this event yet.
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
